/**
 * @file benefitPlanImageDefaults.js
 * @description Shared default benefit image helpers for plan type IDs and plan codes.
 */

import { PlanTypeEnum } from './data/enums';

const FALLBACK_BENEFIT_PLAN_IMAGE = '/benefitPlan/Medical2.png';

const DEFAULT_IMAGE_BY_PLAN_TYPE_ID = {
    [PlanTypeEnum.Medical]: '/benefitPlan/Medical2.png',
    [PlanTypeEnum.Dental]: '/benefitPlan/Dental2.png',
    [PlanTypeEnum.Vision]: '/benefitPlan/Vision2.png',
    [PlanTypeEnum.HealthcareFSA]: '/benefitPlan/HCFSA.png',
    [PlanTypeEnum.DependentCareFSA]: '/benefitPlan/DCFSA2.png',
    [PlanTypeEnum.LimitedPurposeFSA]: '/benefitPlan/LPFSA2.png',
    [PlanTypeEnum.HSA]: '/benefitPlan/HSA2.png',
    [PlanTypeEnum.HRA]: '/benefitPlan/HRA2.png',
    [PlanTypeEnum.LTD]: '/benefitPlan/LTD2.png',
    [PlanTypeEnum.STD]: '/benefitPlan/ShortTermDisability.png',
    [PlanTypeEnum.BasicLife]: '/benefitPlan/BasicLife.png',
    [PlanTypeEnum.VolEELife]: '/benefitPlan/VoluntaryEmployeeLife.png',
    [PlanTypeEnum.VolSpouseLife]: '/benefitPlan/VoluntarySpouseLife.png',
    [PlanTypeEnum.VolChildLife]: '/benefitPlan/VoluntaryChildLife.png',
    [PlanTypeEnum.WholeLife]: '/benefitPlan/WholeLife.png',
    [PlanTypeEnum.HospitalIndemnity]: '/benefitPlan/HospitalIndemnity.png',
    [PlanTypeEnum.CriticalIllness]: '/benefitPlan/CriticalIllness.png',
    [PlanTypeEnum.CriticalIllness2]: '/benefitPlan/CriticalIllness.png',
    [PlanTypeEnum.CriticalIllness3]: '/benefitPlan/CriticalIllness.png',
    [PlanTypeEnum.CriticalIllness4]: '/benefitPlan/CriticalIllness.png',
    [PlanTypeEnum.Accident]: '/benefitPlan/AccidentCoverage.png',
    [PlanTypeEnum.Cancer]: '/benefitPlan/CancerCoverage.png',
    [PlanTypeEnum.Legal]: '/benefitPlan/LegalServices.png',
    [PlanTypeEnum.IdentityTheft]: '/benefitPlan/IdentityTheft.png',
    [PlanTypeEnum.PetInsurance]: '/benefitPlan/PetInsurance.png',
    [PlanTypeEnum.Four01K]: '/benefitPlan/401k.png',
    [PlanTypeEnum.Four01KRoth]: '/benefitPlan/401k.png',
    [PlanTypeEnum.Four03B]: '/benefitPlan/403B.png',
    [PlanTypeEnum.ParkingTransitFSA]: '/benefitPlan/CommuterTransitFSA.png',
    [PlanTypeEnum.EAP]: '/benefitPlan/EAP.png',
    [PlanTypeEnum.AccidentalDeathAndDismemberment]: '/benefitPlan/AD&D.png',
    [PlanTypeEnum.SupplementalChildADAndD]: '/benefitPlan/AD&D.png',
    [PlanTypeEnum.SupplementalEEADAndD]: '/benefitPlan/AD&D.png',
    [PlanTypeEnum.SupplementalSpouseADAndD]: '/benefitPlan/AD&D.png',
    [PlanTypeEnum.VolLTD]: '/benefitPlan/VoluntaryLTD.png',
    [PlanTypeEnum.VolSTD]: '/benefitPlan/VoluntarySTD.png',
};

const DEFAULT_IMAGE_BY_PLAN_CODE = {
    MED: '/benefitPlan/Medical2.png',
    MEDICAL: '/benefitPlan/Medical2.png',
    DENTAL: '/benefitPlan/Dental2.png',
    DEN: '/benefitPlan/Dental2.png',
    VISION: '/benefitPlan/Vision2.png',
    VIS: '/benefitPlan/Vision2.png',
    HCFSA: '/benefitPlan/HCFSA.png',
    FSA: '/benefitPlan/FSA.png',
    DCFSA: '/benefitPlan/DCFSA2.png',
    LPFSA: '/benefitPlan/LPFSA2.png',
    HSA: '/benefitPlan/HSA2.png',
    HRA: '/benefitPlan/HRA2.png',
    LTD: '/benefitPlan/LTD2.png',
    VLIFE: '/benefitPlan/BasicLife.png',
    STD: '/benefitPlan/ShortTermDisability.png',
    VSTD: '/benefitPlan/VoluntarySTD.png',
    BASICLIFE: '/benefitPlan/BasicLife.png',
    BLIFE: '/benefitPlan/BasicLife.png',
    LIFE: '/benefitPlan/BasicLife.png',
    VOLEELIFE: '/benefitPlan/VoluntaryEmployeeLife.png',
    VOLEEL: '/benefitPlan/VoluntaryEmployeeLife.png',
    VEELIFE: '/benefitPlan/VoluntaryEmployeeLife.png',
    EELIFE: '/benefitPlan/VoluntaryEmployeeLife.png',
    VOLSPOUSELIFE: '/benefitPlan/VoluntarySpouseLife.png',
    VOLSPLIFE: '/benefitPlan/VoluntarySpouseLife.png',
    VSPLIFE: '/benefitPlan/VoluntarySpouseLife.png',
    SPLIFE: '/benefitPlan/VoluntarySpouseLife.png',
    VOLCHILDLIFE: '/benefitPlan/VoluntaryChildLife.png',
    VOLCHLIFE: '/benefitPlan/VoluntaryChildLife.png',
    VCHLIFE: '/benefitPlan/VoluntaryChildLife.png',
    CHLIFE: '/benefitPlan/VoluntaryChildLife.png',
    WHOLELIFE: '/benefitPlan/WholeLife.png',
    WLIFE: '/benefitPlan/WholeLife.png',
    HOSPITAL: '/benefitPlan/HospitalIndemnity.png',
    HOSPITALINDEMNITY: '/benefitPlan/HospitalIndemnity.png',
    HOSPINDEM: '/benefitPlan/HospitalIndemnity.png',
    HOSIND: '/benefitPlan/HospitalIndemnity.png',
    HI: '/benefitPlan/HospitalIndemnity.png',
    CRITICALILLNESS: '/benefitPlan/CriticalIllness.png',
    CRITILLNESS: '/benefitPlan/CriticalIllness.png',
    CRITILL: '/benefitPlan/CriticalIllness.png',
    CRITILL2: '/benefitPlan/CriticalIllness.png',
    CRITILL3: '/benefitPlan/CriticalIllness.png',
    CRITILL4: '/benefitPlan/CriticalIllness.png',
    CRITICAL: '/benefitPlan/CriticalIllness.png',
    CI: '/benefitPlan/CriticalIllness.png',
    ACCIDENT: '/benefitPlan/AccidentCoverage.png',
    ACC: '/benefitPlan/AccidentCoverage.png',
    ADANDD: '/benefitPlan/AD&D.png',
    ADD: '/benefitPlan/AD&D.png',
    CANCER: '/benefitPlan/CancerCoverage.png',
    CAN: '/benefitPlan/CancerCoverage.png',
    LEGAL: '/benefitPlan/LegalServices.png',
    LEG: '/benefitPlan/LegalServices.png',
    IDTHEFT: '/benefitPlan/IdentityTheft.png',
    IDENTITYTHEFT: '/benefitPlan/IdentityTheft.png',
    PET: '/benefitPlan/PetInsurance.png',
    PETINSURANCE: '/benefitPlan/PetInsurance.png',
    EAP: '/benefitPlan/EAP.png',
    '401K': '/benefitPlan/401k.png',
    FOUR01K: '/benefitPlan/401k.png',
    '403B': '/benefitPlan/403B.png',
    FOUR03B: '/benefitPlan/403B.png',
    COMMUTERTRANSITFSA: '/benefitPlan/CommuterTransitFSA.png',
    COMMUTERFSA: '/benefitPlan/CommuterTransitFSA.png',
    TRANSPORTATIONFSA: '/benefitPlan/CommuterTransitFSA.png',
    PTFSA: '/benefitPlan/CommuterTransitFSA.png',
    CTFSA: '/benefitPlan/CommuterTransitFSA.png',
    TRANSITFSA: '/benefitPlan/TransitCommuterFSA.png',
    PARKINGTRANSITFSA: '/benefitPlan/CommuterTransitFSA.png',
    VOLLTD: '/benefitPlan/VoluntaryLTD.png',
    VLTD: '/benefitPlan/VoluntaryLTD.png',
    VOLSTD: '/benefitPlan/VoluntarySTD.png',
};

export function normalizePlanCode(planCode) {
    return (planCode || '')
        .toString()
        .trim()
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '');
}

export function getDefaultBenefitImageByPlanTypeId(planTypeId) {
    return (
        DEFAULT_IMAGE_BY_PLAN_TYPE_ID[planTypeId] || FALLBACK_BENEFIT_PLAN_IMAGE
    );
}

export function getDefaultBenefitImageByPlanCode(planCode) {
    const normalizedCode = normalizePlanCode(planCode);
    return DEFAULT_IMAGE_BY_PLAN_CODE[normalizedCode] || null;
}
